<script>
    import { explorerState } from "@app/store/explorerStore.svelte.js";
    import { authState } from "@app/store/authStore.svelte.js";
    import { toast } from "@zerodevx/svelte-toast";
    import * as m from "@app/paraglide/messages.js";
    /* Firebase */
    import { auth } from "@app/configs/firebase.js";
    import db from "@app/configs/firestore.js";
    import { collection, getDocs, query, where, orderBy } from "firebase/firestore";

    let userId = auth.currentUser.uid;
    let teamId = authState.team ? authState.team.id : null;
    let homeDbRef = collection(db, "users", userId, "storage");
    let teamDbRef = teamId ? collection(db, "teams", teamId, "storage") : null;
    let loading = false;

    function isSpecialRootDir(rootDir = explorerState.rootDir) {
        return ["search", "recent", "starred", "tasks"].includes(rootDir);
    }

    // Helper function to check if a folder should be visible to current user based on group permissions
    function canUserAccessFolder(folder) {
        // Admin and Manager roles always have access to all folders
        if (authState.user && (authState.user.role === "admin" || authState.user.role === "manager")) {
            return true;
        }
        
        // If folder has no groups assigned, it's accessible to all team members
        if (!folder.groups || folder.groups.length === 0) {
            return true;
        }
        
        // Check if user is a member of any of the folder's assigned groups
        if (authState.user && authState.user.groups) {
            return folder.groups.some(groupId => authState.user.groups.includes(groupId));
        }
        
        // If user has no groups, they can't access group-restricted folders
        return false;
    }

    /* Get records where folder Id is the records locationId */
    async function getRecords(folderId = null){
        try {
            loading = true;
            explorerState.records = [];
            explorerState.selectedRecords = [];

            if (isSpecialRootDir()) {
                return;
            }

            // console.log("Getting Records for Folder ID: " + folderId);
            let querySnapshot = await getDocs(query(explorerState.rootDir === "personal" ? homeDbRef : teamDbRef, where("locationId", "==", folderId), orderBy("type"), orderBy("name", "asc")));

            querySnapshot.forEach((doc) => {
                const record = {...(doc.data()), rootDir: explorerState.rootDir};
                
                // For personal storage, add all records
                if (explorerState.rootDir === "personal") {
                    explorerState.records = [...explorerState.records, record];
                }
                // For team storage, filter folders by permissions
                else if (explorerState.rootDir === "team") {
                    // If it's a folder, check permissions
                    if (record.type === "folder") {
                        if (canUserAccessFolder(record)) {
                            explorerState.records = [...explorerState.records, record];
                        }
                    } 
                    // Non-folder records are always shown (they get filtered at the folder level)
                    else {
                        explorerState.records = [...explorerState.records, record];
                    }
                }
            });

            // console.log("----RECORDS----");
            // console.log(explorerState.records);
        } catch(err){
            console.log(err);
            toast.push(err.message, {
                classes: ["toast-danger"],
            });
        } finally {
            loading = false;
        }   
    }

    function getRootDirLabel(rootDir) {
        switch (rootDir) {
            case "personal":
                return m.explorer_root_personal_label();
            case "team":
                return m.explorer_root_team_label();
            case "recent":
                return m.explorer_root_recent_label();
            case "starred":
                return m.explorer_root_starred_label();
            case "tasks":
                return m.explorer_root_tasks_label();
            case "search":
                return m.explorer_nav_search();
            default:
                return rootDir;
        }
    }

    function navigateHistory(index) {
        if(index === null){
            explorerState.navHistory = [];
            if (isSpecialRootDir()) {
                return;
            }
            getRecords();
        } else {
            let record = explorerState.navHistory[index];
            
            // Check if this is a team folder and user has access
            if (record.rootDir === "team" && record.type === "folder") {
                if (!canUserAccessFolder(record)) {
                    toast.push(m.explorer_breadcrumbs_no_permission_toast(), {
                        classes: ["toast-danger"],
                    });
                    return;
                }
            }
            
            explorerState.navHistory = explorerState.navHistory.slice(0, index+1);
            getRecords(record.id);
        }
    }
</script>
<div class="col">
    <ol class="breadcrumb">        
        {#if explorerState.navHistory.length === 0}
            <!-- No history just show the current folder we're in -->
            <li class="breadcrumb-item text-capitalize" aria-current="page"><span>{getRootDirLabel(explorerState.rootDir)}</span></li>
        {:else if explorerState.navHistory.length > 2}
            <!-- History more than 2 we show an ellipses to go back and then the last 2 -->
            <li class="breadcrumb-item text-capitalize"><a href="#!/" onclick={() => navigateHistory(explorerState.navHistory.length - 3)}><span>...</span></a></li>
            {#each explorerState.navHistory.slice(explorerState.navHistory.length - 2, explorerState.navHistory.length) as record, i (i)}
                {@const actualIndex = explorerState.navHistory.length - 2 + i}
                {#if i === 1} <!-- Last item -->
                    <li class="breadcrumb-item text-capitalize" aria-current="page"><span>{record.name}</span></li>
                {:else}
                    <li class="breadcrumb-item text-capitalize"><a href="#!/" onclick={() => navigateHistory(actualIndex)}><span>{record.name}</span></a></li>
                {/if}
            {/each}
        {:else}
            <!-- Show all the history -->
            <li class="breadcrumb-item text-capitalize"><a href="#!/" onclick={() => navigateHistory(null)}><span>{getRootDirLabel(explorerState.rootDir)}</span></a></li>
            {#each explorerState.navHistory as record, index (index)}
                {#if index === explorerState.navHistory.length - 1}
                    <li class="breadcrumb-item text-capitalize" aria-current="page"><span>{record.name}</span></li>
                {:else}
                    <li class="breadcrumb-item text-capitalize"><a href="#!/" onclick={() => navigateHistory(index)}><span>{record.name}</span></a></li>
                {/if}
            {/each}
        {/if}
    </ol>
</div>